<template>
  <div class="py-12 bg-white">
    <div class="max-w-5xl mx-auto px-4">
      <!-- Header -->
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold mb-2">
          {{ resolvedContent.title }}
        </h2>
        <p
          v-if="resolvedContent.subtitle"
          class="text-gray-600"
        >
          {{ resolvedContent.subtitle }}
        </p>
      </div>

      <!-- Sidebar + Cards Layout -->
      <div
        v-if="resolvedContent.professions && resolvedContent.professions.length"
        class="flex flex-col md:flex-row gap-6"
      >
        <!-- Left sidebar: profession tabs -->
        <div
          class="flex md:flex-col gap-2 md:w-56 flex-shrink-0 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 hide-scrollbar"
        >
          <button
            v-for="(prof, i) in resolvedContent.professions"
            :key="i"
            class="px-4 py-2.5 rounded-full text-sm font-medium text-left transition-colors whitespace-nowrap"
            :class="
              activeProfession === i
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            "
            @click="selectProfession(i)"
          >
            {{ prof.profession ? prof.profession.name : "\u2014" }}
          </button>
        </div>

        <!-- Right: Cards area -->
        <div class="flex-1 min-w-0">
          <!-- Loading state -->
          <div
            v-if="loading"
            class="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <div
              v-for="n in 4"
              :key="n"
              class="animate-pulse"
            >
              <div class="bg-gray-200 rounded-xl aspect-[3/4] mb-3" />
              <div class="h-3 bg-gray-200 rounded mb-2 w-3/4" />
              <div class="h-2 bg-gray-200 rounded w-1/2" />
            </div>
          </div>

          <!-- Cards grid -->
          <div
            v-else-if="professionals.length"
            class="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <ProfessionalPortraitCard
              v-for="person in professionals"
              :key="person.id"
              :person="person"
              :profession-name="selectedProfessionName"
            />
          </div>

          <!-- Empty state -->
          <div
            v-else
            class="text-center py-12 text-gray-400"
          >
            <p>{{ $t("landing.professionGroups.noProfessionals") }}</p>
          </div>
        </div>
      </div>

      <!-- Description area -->
      <div
        v-if="currentProfession && currentProfession.description"
        class="mt-6 p-4 bg-blue-50 rounded-lg"
      >
        <h3 class="text-2xl font-bold mb-3">
          {{ currentProfession.profession ? currentProfession.profession.name : "" }}
        </h3>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="text-gray-600 leading-relaxed mb-4"
          v-html="currentProfession.description"
        />
        <!-- eslint-enable vue/no-v-html -->
        <a
          v-if="professionPageLink !== '#'"
          :href="professionPageLink"
          target="_blank"
          rel="noopener noreferrer"
        >
          <JoszakiButton size="sm" type="info">
            {{ selectedProfessionName }} {{ $t("landing.professionGroups.searchButton") }}
          </JoszakiButton>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  useContext,
} from "@nuxtjs/composition-api";
import ProfessionalPortraitCard from "./front-page-modules/ProfessionalPortraitCard.vue";
import { useFetchProfessionals } from "~/components/_refactored/profession/useFetchProfessionals";
import { DEFAULT_MODULE_CONTENT } from "~/config/frontPageModuleDefaultContent";

export default {
  name: "PopularProfessionsSection",
  components: {
    ProfessionalPortraitCard,
  },
  props: {
    content: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const { localePath } = useContext();
    const defaults = DEFAULT_MODULE_CONTENT["popular-professions"] || {};
    const fetchProfessionals = useFetchProfessionals();

    const resolvedContent = computed(() => {
      if (!props.content) return defaults;
      return { ...defaults, ...props.content };
    });

    const defaultIndex = computed(() => {
      const idx = parseInt(resolvedContent.value.defaultProfession, 10);
      return isNaN(idx) ? 0 : idx;
    });

    const activeProfession = ref(0);
    const loading = ref(false);
    const professionals = ref([]);
    const cache = {};

    const currentProfession = computed(
      () =>
        (resolvedContent.value.professions || [])[activeProfession.value] ||
        null
    );

    const selectedProfessionName = computed(
      () => currentProfession.value?.profession?.name || ""
    );

    const professionPageLink = computed(() => {
      const seoName = currentProfession.value?.profession?.seoName;
      if (!seoName) return "#";
      return localePath({
        name: "professionPage",
        params: { profession: seoName },
      });
    });

    const loadProfessionals = async () => {
      const prof = currentProfession.value;
      if (!prof?.profession?.seoName) {
        professionals.value = [];
        return;
      }
      const seoName = prof.profession.seoName;

      if (cache[seoName]) {
        professionals.value = cache[seoName];
        return;
      }

      loading.value = true;
      try {
        const result = await fetchProfessionals({
          professionSeoName: seoName,
          citySeoName: "budapest",
          page: 1,
          pageSize: 4,
        });
        const people = (result?.people || []).slice(0, 4);
        cache[seoName] = people;
        professionals.value = people;
      } catch (err) {
        console.error("[PopularProfessions] Failed to fetch:", err);
        professionals.value = [];
      } finally {
        loading.value = false;
      }
    };

    const selectProfession = (index) => {
      activeProfession.value = index;
    };

    watch(activeProfession, () => {
      loadProfessionals();
    });

    onMounted(() => {
      activeProfession.value = defaultIndex.value;
      loadProfessionals();
    });

    return {
      resolvedContent,
      activeProfession,
      currentProfession,
      selectedProfessionName,
      professionals,
      loading,
      professionPageLink,
      selectProfession,
    };
  },
};
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
